import { type FC } from 'react';
import { Link } from 'react-router-dom';
import PublicNavBar from '../components/layout/PublicNavBar';
import PublicFooter from '../components/layout/PublicFooter';
import { usePageMeta } from '../hooks/usePageMeta';

const PrivacyPolicyPage: FC = () => {
  usePageMeta({ title: 'Privacy Policy', description: 'Quanthop privacy policy. How we collect, use, and protect your personal data.' });

  return (
    <div className="min-h-screen bg-[#0a0e14] text-gray-100 font-[Poppins] selection:bg-[#9d4edd]/30">
      <PublicNavBar />

      {/* ════════════════ HERO ════════════════ */}
      <section className="relative pt-36 pb-20 px-4 sm:px-6 lg:px-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0f1520] via-[#0a0e14] to-[#080b11]" />
        <div
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(circle, rgba(255,255,255,0.15) 1px, transparent 1px)',
            backgroundSize: '24px 24px',
          }}
        />

        <div className="max-w-[680px] mx-auto relative z-10 text-center">
          <h1 className="text-3xl sm:text-[2.25rem] md:text-[2.5rem] font-bold leading-[1.1] tracking-[-0.025em] mb-5 text-white">
            Privacy Policy
          </h1>

          <p className="text-[15px] text-gray-300/70 leading-[1.7]">
            How we collect, use, and protect your personal data.
          </p>

          <p className="text-[13px] text-gray-400/60 mt-6">
            Last updated: 8 March 2026
          </p>
        </div>
      </section>

      {/* ════════════════ CONTENT ════════════════ */}
      <section className="py-16 px-4 sm:px-6 lg:px-10 bg-white">
        <div className="max-w-[720px] mx-auto text-gray-700 text-[15px] leading-[1.8]">
          <p className="mb-10 text-gray-500">
            Quanthop (&ldquo;we&rdquo;, &ldquo;our&rdquo;, or
            &ldquo;us&rdquo;) respects your privacy and is committed to
            protecting your personal data. This Privacy Policy explains how we
            collect, use, and protect your information when you use the
            Quanthop website and platform (the &ldquo;Service&rdquo;).
          </p>

          {/* ── 1 ── */}
          <h2 className="text-[18px] font-bold text-gray-950 tracking-[-0.01em] mb-4 mt-14">
            1. Information We Collect
          </h2>
          <p className="mb-3">
            We may collect the following types of information.
          </p>

          <h3 className="text-[16px] font-semibold text-gray-900 mb-3 mt-8">
            Account Information
          </h3>
          <p className="mb-3">When you create an account we may collect:</p>
          <ul className="list-disc pl-6 space-y-1.5 mb-4">
            <li>name</li>
            <li>email address</li>
            <li>account credentials</li>
            <li>billing information</li>
          </ul>

          <h3 className="text-[16px] font-semibold text-gray-900 mb-3 mt-8">
            Usage Data
          </h3>
          <p className="mb-3">When you use the platform we may collect:</p>
          <ul className="list-disc pl-6 space-y-1.5 mb-4">
            <li>IP address</li>
            <li>browser type</li>
            <li>device information</li>
            <li>usage activity within the platform</li>
            <li>log data</li>
          </ul>

          <h3 className="text-[16px] font-semibold text-gray-900 mb-3 mt-8">
            Research Data
          </h3>
          <p className="mb-3">
            The platform may process user-submitted data including:
          </p>
          <ul className="list-disc pl-6 space-y-1.5 mb-4">
            <li>trading strategies</li>
            <li>parameter configurations</li>
            <li>uploaded datasets</li>
            <li>research results</li>
          </ul>
          <p>Users retain ownership of their research data.</p>

          {/* ── 2 ── */}
          <h2 className="text-[18px] font-bold text-gray-950 tracking-[-0.01em] mb-4 mt-14">
            2. How We Use Your Information
          </h2>
          <p className="mb-3">We use personal data to:</p>
          <ul className="list-disc pl-6 space-y-1.5 mb-4">
            <li>provide and operate the platform</li>
            <li>manage user accounts</li>
            <li>process payments</li>
            <li>improve platform performance</li>
            <li>monitor platform security</li>
            <li>communicate important service updates</li>
            <li>comply with legal obligations</li>
          </ul>
          <p className="font-medium text-gray-900">
            We do not sell personal data to third parties.
          </p>

          {/* ── 3 ── */}
          <h2 className="text-[18px] font-bold text-gray-950 tracking-[-0.01em] mb-4 mt-14">
            3. Legal Basis for Processing (GDPR)
          </h2>
          <p className="mb-3">
            Under the General Data Protection Regulation (GDPR), we process
            personal data based on:
          </p>
          <ul className="list-disc pl-6 space-y-1.5 mb-4">
            <li>
              <strong>Contractual necessity</strong> &mdash; to provide the
              Service you requested
            </li>
            <li>
              <strong>Legitimate interest</strong> &mdash; to improve and
              secure the platform
            </li>
            <li>
              <strong>Legal obligation</strong> &mdash; to comply with
              applicable laws
            </li>
          </ul>

          {/* ── 4 ── */}
          <h2 className="text-[18px] font-bold text-gray-950 tracking-[-0.01em] mb-4 mt-14">
            4. Data Storage and Security
          </h2>
          <p className="mb-4">
            We implement appropriate security measures to protect personal data
            from unauthorised access, loss, or misuse.
          </p>
          <p className="mb-4">
            Data may be stored on secure infrastructure providers such as cloud
            hosting services.
          </p>
          <p>
            While we take reasonable precautions, no system can guarantee
            absolute security.
          </p>

          {/* ── 5 ── */}
          <h2 className="text-[18px] font-bold text-gray-950 tracking-[-0.01em] mb-4 mt-14">
            5. Data Retention
          </h2>
          <p className="mb-3">
            We retain personal data only as long as necessary to:
          </p>
          <ul className="list-disc pl-6 space-y-1.5 mb-4">
            <li>provide the Service</li>
            <li>comply with legal obligations</li>
            <li>resolve disputes</li>
            <li>enforce our agreements</li>
          </ul>
          <p>
            Users may request deletion of their account and personal data.
          </p>

          {/* ── 6 ── */}
          <h2 className="text-[18px] font-bold text-gray-950 tracking-[-0.01em] mb-4 mt-14">
            6. Your Rights (GDPR)
          </h2>
          <p className="mb-3">Under GDPR you have the right to:</p>
          <ul className="list-disc pl-6 space-y-1.5 mb-4">
            <li>access your personal data</li>
            <li>request correction of inaccurate data</li>
            <li>request deletion of personal data</li>
            <li>restrict processing</li>
            <li>request data portability</li>
            <li>object to processing</li>
          </ul>
          <p>
            To exercise these rights, contact us using the details below.
          </p>

          {/* ── 7 ── */}
          <h2 className="text-[18px] font-bold text-gray-950 tracking-[-0.01em] mb-4 mt-14">
            7. Third-Party Services
          </h2>
          <p className="mb-3">
            We may use third-party services to operate the platform, including:
          </p>
          <ul className="list-disc pl-6 space-y-1.5 mb-4">
            <li>cloud infrastructure providers</li>
            <li>payment processors</li>
            <li>analytics services</li>
          </ul>
          <p>
            These providers may process personal data only as necessary to
            provide their services.
          </p>

          {/* ── 8 ── */}
          <h2 className="text-[18px] font-bold text-gray-950 tracking-[-0.01em] mb-4 mt-14">
            8. Cookies
          </h2>
          <p className="mb-4">
            Quanthop uses cookies and similar technologies to improve the user
            experience.
          </p>
          <p>
            More information is available in our{' '}
            <Link to="/cookies" className="text-[#9d4edd] hover:underline">
              Cookie Policy
            </Link>
            .
          </p>

          {/* ── 9 ── */}
          <h2 className="text-[18px] font-bold text-gray-950 tracking-[-0.01em] mb-4 mt-14">
            9. Changes to This Policy
          </h2>
          <p className="mb-4">
            We may update this Privacy Policy periodically.
          </p>
          <p>
            When updates occur, the revised version will be posted on this page.
          </p>

          {/* ── 10 ── */}
          <h2 className="text-[18px] font-bold text-gray-950 tracking-[-0.01em] mb-4 mt-14">
            10. Contact
          </h2>
          <p className="mb-3">For privacy inquiries:</p>
          <ul className="space-y-1.5">
            <li>
              <strong>Email:</strong>{' '}
              <a
                href="mailto:privacy@quanthop.com"
                className="text-[#9d4edd] hover:underline"
              >
                privacy@quanthop.com
              </a>
            </li>
            <li>
              <strong>Website:</strong>{' '}
              <a
                href="https://quanthop.com"
                className="text-[#9d4edd] hover:underline"
              >
                quanthop.com
              </a>
            </li>
          </ul>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default PrivacyPolicyPage;
